/**
 * TinyML Chart Window Manager
 * センサーデータのリアルタイム可視化
 */

class TinyMLChartWindow {
    constructor() {
        this.chartWindow = null;
        this.chartUpdateInterval = null;
        this.chartDataBuffer = {
            x: [],
            y: [],
            z: [],
            timestamps: []
        };
        this.maxChartPoints = 100;  // 表示する最大ポイント数
        // 現在表示中のセンサー種別。setSensorType で更新され、_readyHandler から
        // 子ウィンドウ準備完了直後にも反映される (open() 直後の取りこぼし防止)。
        this.sensorType = 'accelerometer';
    }

    /**
     * グラフウィンドウを開く (idempotent)
     *
     * setupSensor の再実行などで stopStreaming → startStreaming のサイクルが
     * 走ると、 stopStreaming 内の chartWindow.stop() で更新インターバルが
     * 殺されてしまう。その後 open() が呼ばれても、既存ウィンドウが存在すると
     * 以前は alert を出して早期 return していたため、更新インターバルが
     * 死んだまま放置され「グラフが固まる」現象になっていた。
     *
     * 既存ウィンドウがあれば focus + 更新ループ再起動だけして静かに返す。
     */
    open() {
        if (this.chartWindow && !this.chartWindow.closed) {
            try { this.chartWindow.focus(); } catch (e) { /* focus 失敗は無視 */ }
            // 更新インターバルが止まっていたら再起動 (重要)
            if (!this.chartUpdateInterval) {
                this._startUpdate();
            }
            return;
        }
        
        const width = 900;
        const height = 600;
        const left = (screen.width - width) / 2;
        const top = (screen.height - height) / 2;
        
        this.chartWindow = window.open(
            '',
            'TinyMLChart',
            `width=${width},height=${height},left=${left},top=${top},resizable=yes`
        );
        
        if (!this.chartWindow) {
            alert('ポップアップがブロックされました。ブラウザの設定を確認してください。');
            return;
        }
        
        // 子ウィンドウからの準備完了メッセージを待つ
        this._readyHandler = (event) => {
            if (event.data && event.data.type === 'chartReady') {
                window.removeEventListener('message', this._readyHandler);
                // 子ウィンドウが準備完了したら、現在のセンサー種別を反映する
                // (open() より前に setSensorType されていたケースを救う)
                this._postSensorConfig(this.sensorType);
                this._startUpdate();
            }
        };
        window.addEventListener('message', this._readyHandler);
        
        this._injectHTML();
    }

    /**
     * HTMLとChart.jsを注入
     */
    _injectHTML() {
        this.chartWindow.document.write(`
<!DOCTYPE html>
<html>
<head>
    <meta charset="UTF-8">
    <title>TinyML センサーデータ可視化</title>
    <script src="https://cdn.jsdelivr.net/npm/chart.js@4.4.0"></script>
    <style>
        body {
            margin: 0;
            padding: 20px;
            font-family: Arial, sans-serif;
            background: #f5f5f5;
        }
        .container {
            max-width: 100%;
            margin: 0 auto;
        }
        h1 {
            color: #FF6B35;
            text-align: center;
            margin-bottom: 20px;
            font-size: 24px;
        }
        .info {
            background: white;
            padding: 15px;
            border-radius: 8px;
            margin-bottom: 20px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .info-item {
            display: inline-block;
            margin-right: 20px;
            font-size: 14px;
        }
        .info-label {
            font-weight: bold;
            color: #666;
        }
        .info-value {
            color: #333;
            font-family: monospace;
            font-size: 13px;
        }
        .chart-container {
            background: white;
            padding: 20px;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            height: 400px;
        }
        .status {
            text-align: center;
            margin-top: 15px;
            color: #666;
            font-size: 12px;
        }
        .status-good {
            color: #4CAF50;
        }
        /* 録音オーバーレイ
         * - countdown: 画面中央に大きく表示(準備中なのでチャートを見る必要はない)
         * - recording / done: 右上の小さなバッジ(チャートを隠さないため)
         */
        #recordingOverlay {
            position: fixed;
            top: 0; left: 0; right: 0; bottom: 0;
            display: none;
            pointer-events: none;
            z-index: 100;
        }
        #recordingOverlay .panel {
            background: rgba(255, 255, 255, 0.97);
            border-radius: 16px;
            text-align: center;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
        }

        /* === countdown: 中央デカ表示 === */
        #recordingOverlay.state-countdown {
            align-items: center;
            justify-content: center;
        }
        #recordingOverlay.state-countdown .panel {
            border: 6px solid #2196F3;
            padding: 36px 48px;
            min-width: 340px;
        }
        #recordingOverlay.state-countdown #recBigText {
            font-size: 96px;
            color: #2196F3;
        }
        #recordingOverlay.state-countdown #recSubText { font-size: 24px; }
        #recordingOverlay.state-countdown #recDetailText { font-size: 18px; margin-top: 8px; }

        /* === recording / done: 右上のコンパクトバッジ === */
        #recordingOverlay.state-recording,
        #recordingOverlay.state-done {
            align-items: flex-start;
            justify-content: flex-end;
            padding: 16px 20px;
        }
        #recordingOverlay.state-recording .panel,
        #recordingOverlay.state-done .panel {
            border: 3px solid #F44336;
            padding: 10px 16px;
            min-width: 0;
            text-align: left;
            display: flex;
            flex-direction: column;
            gap: 2px;
        }
        #recordingOverlay.state-recording .panel { animation: pulse 1s infinite; }
        #recordingOverlay.state-done .panel { border-color: #4CAF50; }

        #recordingOverlay.state-recording #recBigText,
        #recordingOverlay.state-done #recBigText {
            font-size: 18px;
            margin: 0;
            font-weight: bold;
        }
        #recordingOverlay.state-recording #recBigText { color: #F44336; }
        #recordingOverlay.state-done #recBigText { color: #4CAF50; }
        #recordingOverlay.state-recording #recSubText,
        #recordingOverlay.state-done #recSubText {
            font-size: 13px;
            color: #333;
            margin: 0;
            line-height: 1.2;
        }
        #recordingOverlay.state-recording #recDetailText,
        #recordingOverlay.state-done #recDetailText {
            font-size: 12px;
            color: #666;
            margin: 0;
        }

        @keyframes pulse {
            0%, 100% { box-shadow: 0 4px 16px rgba(244, 67, 54, 0.4); }
            50%      { box-shadow: 0 4px 24px rgba(244, 67, 54, 0.8); }
        }
        #recBigText { line-height: 1; margin: 0; font-weight: bold; }
        #recSubText { margin: 0; }
        #recDetailText { margin: 0; }
    </style>
</head>
<body>
    <div id="recordingOverlay">
        <div class="panel">
            <div id="recBigText">3</div>
            <div id="recSubText">準備してください</div>
            <div id="recDetailText"></div>
        </div>
    </div>
    <div class="container">
        <h1>📊 TinyML センサーデータ可視化</h1>
        
        <div class="info">
            <div class="info-item">
                <span class="info-label">X軸:</span>
                <span class="info-value" id="current-x">-</span>
            </div>
            <div class="info-item">
                <span class="info-label">Y軸:</span>
                <span class="info-value" id="current-y">-</span>
            </div>
            <div class="info-item">
                <span class="info-label">Z軸:</span>
                <span class="info-value" id="current-z">-</span>
            </div>
            <div class="info-item">
                <span class="info-label">更新:</span>
                <span class="info-value" id="last-update">-</span>
            </div>
        </div>
        
        <div class="chart-container">
            <canvas id="sensorChart"></canvas>
        </div>
        
        <div class="status status-good">
            リアルタイムで最新100サンプルを表示中...
        </div>
    </div>
    
    <script>
        const ctx = document.getElementById('sensorChart');
        let sampleCount = 0;

        const chart = new Chart(ctx, {
            type: 'line',
            data: {
                labels: [],
                datasets: [
                    {
                        label: 'X軸',
                        data: [],
                        borderColor: 'rgb(255, 99, 132)',
                        backgroundColor: 'rgba(255, 99, 132, 0.1)',
                        borderWidth: 2,
                        tension: 0.4,
                        pointRadius: 0
                    },
                    {
                        label: 'Y軸',
                        data: [],
                        borderColor: 'rgb(54, 162, 235)',
                        backgroundColor: 'rgba(54, 162, 235, 0.1)',
                        borderWidth: 2,
                        tension: 0.4,
                        pointRadius: 0
                    },
                    {
                        label: 'Z軸',
                        data: [],
                        borderColor: 'rgb(75, 192, 192)',
                        backgroundColor: 'rgba(75, 192, 192, 0.1)',
                        borderWidth: 2,
                        tension: 0.4,
                        pointRadius: 0
                    }
                ]
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                animation: false,
                scales: {
                    x: {
                        display: true,
                        title: {
                            display: true,
                            text: '時間'
                        },
                        ticks: {
                            maxTicksLimit: 10
                        }
                    },
                    y: {
                        display: true,
                        title: {
                            display: true,
                            text: '加速度 [g]'
                        },
                        min: -2,
                        max: 2
                    }
                },
                plugins: {
                    legend: {
                        display: true,
                        position: 'top'
                    },
                    tooltip: {
                        mode: 'index',
                        intersect: false
                    }
                },
                interaction: {
                    mode: 'nearest',
                    axis: 'x',
                    intersect: false
                }
            }
        });

        // 親ウィンドウに準備完了を通知
        if (window.opener) {
            window.opener.postMessage({ type: 'chartReady' }, '*');
        }

        // 録画オーバーレイ更新ヘルパー
        function applyRecordingState(payload) {
            const overlay = document.getElementById('recordingOverlay');
            const bigText = document.getElementById('recBigText');
            const subText = document.getElementById('recSubText');
            const detail = document.getElementById('recDetailText');
            overlay.classList.remove('state-countdown', 'state-recording', 'state-done');
            switch (payload.state) {
                case 'countdown':
                    overlay.style.display = 'flex';
                    overlay.classList.add('state-countdown');
                    bigText.textContent = String(payload.secondsLeft);
                    subText.textContent = '準備してください';
                    detail.textContent = payload.label ? 'これから「' + payload.label + '」を収集します' : '';
                    break;
                case 'recording':
                    // 右上コーナーのコンパクトバッジ。チャートが見えるよう文言は最小限
                    overlay.style.display = 'flex';
                    overlay.classList.add('state-recording');
                    bigText.textContent = '● REC';
                    subText.textContent = payload.label ? '「' + payload.label + '」' : '';
                    detail.textContent = (typeof payload.secondsLeft === 'number')
                        ? 'のこり ' + payload.secondsLeft + ' 秒'
                        : (payload.duration ? payload.duration + ' 秒間' : '');
                    break;
                case 'done':
                    // こちらも右上バッジ。完了の余韻表示なので合計サンプル数だけ出す
                    overlay.style.display = 'flex';
                    overlay.classList.add('state-done');
                    bigText.textContent = '✓ 完了';
                    subText.textContent = payload.label ? '「' + payload.label + '」' : '';
                    detail.textContent = (payload.justCollected != null)
                        ? '+' + payload.justCollected + ' (合計 ' + payload.total + ')'
                        : '';
                    break;
                case 'idle':
                default:
                    overlay.style.display = 'none';
                    break;
            }
        }

        // センサー種別に応じてデータセットのラベルとY軸を切替
        function applySensorConfig(sensorType) {
            if (sensorType === 'microphone') {
                chart.data.datasets[0].label = '音量(RMS)';
                chart.data.datasets[1].label = 'ゼロ交差率';
                chart.data.datasets[2].label = 'ピーク';
                chart.options.scales.y.title.text = '正規化値 (0..1)';
                chart.options.scales.y.min = 0;
                chart.options.scales.y.max = 1;
            } else {
                chart.data.datasets[0].label = 'X軸';
                chart.data.datasets[1].label = 'Y軸';
                chart.data.datasets[2].label = 'Z軸';
                chart.options.scales.y.title.text = '加速度 [g]';
                chart.options.scales.y.min = -2;
                chart.options.scales.y.max = 2;
            }
            chart.update('none');
        }

        // 親ウィンドウからのメッセージを受信
        window.addEventListener('message', (event) => {
            if (event.data.type === 'recordingState') {
                applyRecordingState(event.data.payload || {});
                return;
            }
            if (event.data.type === 'sensorConfig') {
                applySensorConfig(event.data.sensorType);
                return;
            }
            if (event.data.type === 'updateChart') {
                const { x, y, z, timestamps } = event.data;
                
                if (!x || x.length === 0) return;
                
                // チャートデータ更新
                chart.data.labels = timestamps;
                chart.data.datasets[0].data = x;
                chart.data.datasets[1].data = y;
                chart.data.datasets[2].data = z;
                chart.update('none');
                
                // 現在値表示
                if (x.length > 0) {
                    const lastIdx = x.length - 1;
                    document.getElementById('current-x').textContent = x[lastIdx].toFixed(3);
                    document.getElementById('current-y').textContent = y[lastIdx].toFixed(3);
                    document.getElementById('current-z').textContent = z[lastIdx].toFixed(3);
                    
                    const now = new Date();
                    document.getElementById('last-update').textContent = 
                        now.getHours().toString().padStart(2, '0') + ':' +
                        now.getMinutes().toString().padStart(2, '0') + ':' +
                        now.getSeconds().toString().padStart(2, '0');
                }
            }
        });
    </script>
</body>
</html>
        `);
        
        this.chartWindow.document.close();
    }

    /**
     * 定期更新を開始
     */
    _startUpdate() {
        if (this.chartUpdateInterval) {
            clearInterval(this.chartUpdateInterval);
        }
        
        this.chartUpdateInterval = setInterval(() => {
            if (this.chartWindow && !this.chartWindow.closed) {
                this._sendData();
            } else {
                this.stop();
            }
        }, 200);  // 5Hz更新（200ms間隔）
    }

    /**
     * グラフにデータを送信
     */
    _sendData() {
        if (!this.chartWindow || this.chartWindow.closed) return;

        try {
            this.chartWindow.postMessage({
                type: 'updateChart',
                x: this.chartDataBuffer.x,
                y: this.chartDataBuffer.y,
                z: this.chartDataBuffer.z,
                timestamps: this.chartDataBuffer.timestamps
            }, '*');
        } catch (error) {
            // エラー時は静かに無視
        }
    }

    /**
     * センサー種別を切り替える。チャートのデータセットラベルとY軸範囲を、
     * 加速度センサー (3軸 [g], -2..2) かマイク (RMS/ZCR/Peak, 0..1) に合わせて
     * 更新する。chartWindow が未起動の場合は this.sensorType に覚えておき、
     * 子ウィンドウ準備完了時に _readyHandler が反映する。
     */
    setSensorType(sensorType) {
        this.sensorType = sensorType;
        this._postSensorConfig(sensorType);
    }

    _postSensorConfig(sensorType) {
        if (!this.chartWindow || this.chartWindow.closed) return;
        try {
            this.chartWindow.postMessage({
                type: 'sensorConfig',
                sensorType: sensorType
            }, '*');
        } catch (e) {
            // silent
        }
    }

    /**
     * 録音状態オーバーレイを更新する。chartWindow が開いていない時は何もしない。
     * payload.state: 'countdown' | 'recording' | 'done' | 'idle'
     */
    setRecordingState(payload) {
        if (!this.chartWindow || this.chartWindow.closed) return;
        try {
            this.chartWindow.postMessage({
                type: 'recordingState',
                payload: payload
            }, '*');
        } catch (error) {
            // silent
        }
    }

    /**
     * データポイントを追加
     */
    addDataPoint(x, y, z) {
        const now = new Date();
        const timeStr = now.getHours().toString().padStart(2, '0') + ':' +
                       now.getMinutes().toString().padStart(2, '0') + ':' +
                       now.getSeconds().toString().padStart(2, '0') + '.' +
                       Math.floor(now.getMilliseconds() / 100);
        
        this.chartDataBuffer.x.push(x);
        this.chartDataBuffer.y.push(y);
        this.chartDataBuffer.z.push(z);
        this.chartDataBuffer.timestamps.push(timeStr);
        
        // 最大ポイント数を超えたら古いデータを削除
        if (this.chartDataBuffer.x.length > this.maxChartPoints) {
            this.chartDataBuffer.x.shift();
            this.chartDataBuffer.y.shift();
            this.chartDataBuffer.z.shift();
            this.chartDataBuffer.timestamps.shift();
        }
    }

    /**
     * グラフウィンドウを閉じる
     */
    close() {
        this.stop();

        if (this._readyHandler) {
            window.removeEventListener('message', this._readyHandler);
            this._readyHandler = null;
        }

        if (this.chartWindow && !this.chartWindow.closed) {
            this.chartWindow.close();
        }
        
        this.chartWindow = null;
    }

    /**
     * 更新を停止
     */
    stop() {
        if (this.chartUpdateInterval) {
            clearInterval(this.chartUpdateInterval);
            this.chartUpdateInterval = null;
        }
        
        // バッファをクリア
        this.chartDataBuffer = {
            x: [],
            y: [],
            z: [],
            timestamps: []
        };
    }

    /**
     * グラフウィンドウが開いているか確認
     */
    isOpen() {
        return this.chartWindow && !this.chartWindow.closed;
    }
}

module.exports = TinyMLChartWindow;